import * as d3 from 'd3';
import { exportSvgAsPng, getClutteredElements, keyValueTitleTooltip, placeTooltip } from '../../vis-shared';

const css = `
  .cell {
    transition: all 0 .3s;
  }
  .hovered .cell {
    fill-opacity: 1;
    stroke-opacity: 1;
  }
  .hovered .cell.hovered {
    fill-opacity: 1;
    stroke-opacity: 1;
  },
`;

export default class DateHeatmap {
  constructor(config) {
    this.config = config;
    this.target = config.target;
    this.data = config.data;
    this.margin = config.margin || { top: 30, right: 20, bottom: 20, left: 100 };

    this.svg = d3.select(this.target).style('position', 'relative').append('svg').classed('date-heatmap', true);

    this.svg.append('style').text(css);

    this.chartSelection = this.svg.append('g').attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    this.tooltip = d3
      .select('body')
      .append('div')
      .classed('tooltip tooltip-arrow-bottom left-edge bg-gray-800 px-4 py-2 rounded-md shadow-md text-white', true)
      .style('visibility', 'hidden')
      .style('width', '180px')
      .style('font-size', '0.8em')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('z-index', 150)
      .style('transition', 'all ease-out 0.1s');
  }

  setSize() {
    const domWidth = Math.floor(parseInt(d3.select(this.target).style('width'), 10));
    const width = this.config.width || domWidth;
    const height = this.config.height || 200;
    this.size = {
      width,
      height,
    };

    this.chartSize = {
      width: width - this.margin.left - this.margin.right,
      height: height - this.margin.top - this.margin.bottom,
    };

    this.svg.attr('width', width).attr('height', height);
  }

  setDataAndScales() {
    const minDate = this.config.minDate || d3.min(this.data, (d) => d.date);
    const maxDate = this.config.maxDate || d3.max(this.data, (d) => d.date);
    const minCount = this.config.minCount || 0;
    const maxCount = this.config.maxCount || d3.max(this.data, (d) => d.count);

    this.weeks = d3.timeWeeks(minDate, maxDate).map((d, i) => ({
      index: i,
      date: d,
    }));

    // x scale for weeks
    this.xScale = d3
      .scaleBand()
      .domain(this.weeks.map((d) => d.index))
      .range([0, this.chartSize.width])
      .paddingInner(0.2);

    // y scale band for each day of the week
    this.yScale = d3.scaleBand().domain(d3.range(7)).range([0, this.chartSize.height]).paddingInner(0.2);

    // color scale for count
    // use hsl interpolation to get a range of colors from white to purple-blue
    const colorInterpolation = d3.interpolateHsl('#c7d2fe', '#4338ca');
    const colorMapScale = d3.scaleLinear().domain([minCount, maxCount]);
    this.colorScale = (d) => colorInterpolation(colorMapScale(d));
    this.borderStroke = colorInterpolation(0);

    const allDays = d3.timeDays(minDate, maxDate);
    // for each day, get the count from the data
    // create map from data for quick lookup
    this.dataMap = this.data.reduce((acc, d) => {
      acc[d.date] = d.count;
      return acc;
    }, {});

    // create array of objects with date and count
    this.dataArray = allDays.map((d) => {
      return {
        date: d,
        count: this.dataMap[d] || 0,
        weekIndex: d3.timeWeek.count(minDate, d),
      };
    });
  }

  getCellColor(d) {
    if (d.count === 0) {
      return '#fcfcfc';
    }
    return this.colorScale(d.count);
  }

  render({ data }) {
    if (data) {
      this.data = data;
    }
    this.chartSelection.html('');
    this.setSize();
    this.setDataAndScales();

    const { width, height } = this.chartSize;

    const dayLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // create day labels
    this.svg
      .selectAll('.day-label')
      .data(dayLabels)
      .enter()
      .append('text')
      .classed('day-label', true)
      .attr('x', this.margin.left - 11)
      .attr('y', (d, i) => this.margin.top + this.yScale(i) + this.yScale.bandwidth() / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', 'end')
      .attr('font-size', '0.7em')
      .attr('font-weight', 'semibold')
      .attr('fill', '#2d3748')
      .text((d) => d);

    // create week labels
    this.svg
      .selectAll('.week-label')
      .data(this.weeks)
      .enter()
      .append('text')
      .classed('week-label', true)
      .attr('x', (d) => this.margin.left + this.xScale(d.index) + this.xScale.bandwidth() / 2)
      .attr('y', this.margin.top - 4)
      .attr('dy', '-0.5em')
      .attr('text-anchor', 'middle')
      .attr('font-size', '0.7em')
      .attr('font-weight', 'semibold')
      .attr('fill', '#2d3748')
      .text((d) => d3.timeFormat('%b %d')(d.date));

    getClutteredElements(this.svg.selectAll('.week-label'), width, 2).remove();

    // create heatmap
    const cells = this.chartSelection
      .selectAll('.cell')
      .data(this.dataArray)
      .enter()
      .append('g')
      .classed('cell', true)
      .attr('transform', (d) => `translate(${this.xScale(d.weekIndex)}, ${this.yScale(d.date.getDay())})`);

    // create two rectangles per cell, one for the background as a hover target

    const hoverTargets = cells
      .append('rect')
      .attr('x', -(this.xScale.padding() * this.xScale.bandwidth()) / 2)
      .attr('y', -(this.yScale.padding() * this.yScale.bandwidth()) / 2)
      .attr('width', this.xScale.bandwidth() + this.xScale.padding() * this.xScale.bandwidth() * 1.25)
      .attr('height', this.yScale.bandwidth() + this.yScale.padding() * this.yScale.bandwidth() * 1.25)
      .attr('fill', 'white');

    const displayRects = cells
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', this.xScale.bandwidth())
      .attr('height', this.yScale.bandwidth())
      .attr('fill', (d) => this.getCellColor(d))
      // rounded
      .attr('rx', 2)
      .attr('ry', 2)
      // light border
      .attr('stroke', this.borderStroke)
      .attr('stroke-width', 1)
      .attr('stroke-opacity', 0.2);

    /* eslint-disable */
    const self = this;
    cells
      .on('mouseover', function (event, d) {
        self.handleCellMouseover(this, d);
      })
      .on('mouseout', function (event, d) {
        self.handleCellMouseout(this);
      })
      .on('click', function (event, d) {
        self.handleCellClick(this, d);
      });

    /* eslint-enable */

    // .on('mouseover', (d) => {
    //   this.tooltip.html(`<div><strong>${d3.timeFormat('%b %d, %Y')(d.date)}</strong></div><div>${d.count}</div>`).style('visibility', 'visible');
    // })
    // .on('mousemove', (d) => {
    //   this.tooltip.style('top', `${d3.event.pageY - 10}px`).style('left', `${d3.event.pageX + 10}px`);
    // })
    // .on('mouseout', (d) => {
    //   this.tooltip.style('visibility', 'hidden');
    // });
  }

  handleCellMouseover(eventTarget, d) {
    if (!d.count) {
      return;
    }
    const x = this.xScale(d.weekIndex);
    const y = this.yScale(d.date.getDay());

    this.svg.classed('hovered', true);
    d3.select(eventTarget).classed('hovered', true);
    if (this.config.cellTooltip) {
      this.config.cellTooltip(this.tooltip, d, this);
    } else {
      const label = d3.timeFormat('%b %d, %Y')(d.date);
      keyValueTitleTooltip(this.tooltip, label, {
        'Daily Active Users': d.count,
      });
    }

    placeTooltip(this.tooltip, d3.select(this.target), {
      x: x + this.xScale.bandwidth() / 2 + this.margin.left,
      y: this.margin.top + y - 16,
      position: 'relative',
      fitInContainer: false,
    });
    this.tooltip.style('visibility', 'visible');
  }

  handleCellMouseout(eventTarget) {
    this.svg.classed('hovered', false);
    d3.select(eventTarget).classed('hovered', false);
    this.tooltip.style('visibility', 'hidden');
  }

  exportToPng() {
    const customCss = `text { font-family: 'Hanken Grotesk', sans-serif }`;
    exportSvgAsPng(this.svg.node(), {
      customCss,
      scale: 3,
      fileName: this.exportFileName,
      title: this.exportTitle || undefined,
    });
  }
}
